import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './PaginaInicio.css';
import './HomeworkPage.css';
import './TeacherContent.css';
import Cabecera from '../Others/Cabecera';
import PiePagina from '../Others/PiePagina';
import {
  subirArchivo,
  obtenerArchivosPorMatricula,
  descargarArchivo,
  eliminarArchivo,
} from './constants';
import { useData } from './DataContext';
import BarraSuperior from '../Others/BarraSuperior';
import BarraInferior from '../Others/BarraInferior';
import BarraInferiorPersonalizadaTeacher from './BarraInferiorPersonalizadaTeacher';

const barra_inferior = (
  <BarraInferior contenido={<BarraInferiorPersonalizadaTeacher />} />
);

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB (ajusta según tu backend)
const ALLOWED_EXTS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'txt', 'zip', 'rar'];

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
}

function extFromName(name) {
  const parts = (name || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function mapAxiosError(error) {
  // Errores con respuesta del servidor (tienen status)
  if (error?.response) {
    const { status, data, headers } = error.response;
    const serverMsg =
      (typeof data === 'string' && data) ||
      data?.mensaje || data?.message || data?.error ||
      '';

    // Casos más frecuentes en tus endpoints
    if (status === 400) return serverMsg || 'Solicitud inválida. Revisa los campos requeridos.';
    if (status === 401) return serverMsg || 'No autorizado. Inicia sesión nuevamente.';
    if (status === 403) return serverMsg || 'Acceso denegado. No tienes permisos para esta acción.';
    if (status === 404) return serverMsg || 'Ruta o recurso no encontrado en el servidor.';
    if (status === 409) return serverMsg || 'Ya existe un archivo con ese nombre o conflicto de datos.';
    if (status === 413) return serverMsg || 'El archivo excede el tamaño permitido por el servidor.';
    if (status === 415) return serverMsg || 'Tipo de archivo no permitido por el servidor.';
    if (status === 422) return serverMsg || 'Datos inválidos. Revisa el formato y vuelve a intentar.';
    if (status >= 500) return serverMsg || 'Error interno en el servidor. Intenta más tarde.';

    return serverMsg || `Error ${status} al procesar la solicitud.`;
  }

  // Errores sin respuesta (CORS, timeout, red caída)
  if (error?.code === 'ECONNABORTED') {
    return 'La solicitud tardó demasiado. Intenta de nuevo o verifica tu conexión.';
  }
  if (error?.message?.toLowerCase().includes('network error')) {
    // Suele ser CORS o caída de red
    return 'No hubo respuesta del servidor (posible CORS o red). Verifica que la API esté accesible.';
  }
  if (error?.request) {
    // Hubo request pero no respuesta (status 0 en navegador)
    return 'No hubo respuesta del servidor. Revisa tu conexión o los permisos CORS.';
  }

  // Cualquier otro caso (configuración, etc.)
  return `Error al preparar la solicitud: ${error?.message || 'desconocido'}`;
}


export const TeacherContent = () => {
  const { dataClase } = useData();
  const [archivo, setArchivo] = useState(null);
  const [tipoArchivo, setTipoArchivo] = useState('pdf');
  const [archivos, setArchivos] = useState([]);

  // Mensajes
  const [msg, setMsg] = useState({ type: '', text: '' }); // type: success | error | info
  const [listMsg, setListMsg] = useState({ type: '', text: '' });

  // Progreso
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
const [highlightId, setHighlightId] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    obtenerContenido();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const obtenerContenido = async () => {
    setListMsg({ type: 'info', text: 'Cargando contenido...' });
    try {
      const response = await axios.get(obtenerArchivosPorMatricula(dataClase.matricula_clase));
      setArchivos(response.data || []);
      setListMsg({ type: '', text: '' });
    } catch (error) {
      console.error('Error al obtener el contenido:', error);
      setListMsg({ type: 'error', text: 'Error al obtener el contenido.' });
    }
  };

  const handleArchivoChange = (event) => {
    const file = event.target.files?.[0];
    setMsg({ type: '', text: '' });
    setProgress(0);

    if (!file) {
      setArchivo(null);
      setTipoArchivo('pdf');
      return;
    }

    const ext = extFromName(file.name);
    if (!ALLOWED_EXTS.includes(ext)) {
      setArchivo(null);
      setTipoArchivo('pdf');
      setMsg({
        type: 'error',
        text: `Tipo de archivo no permitido: .${ext}. Permitidos: ${ALLOWED_EXTS.join(', ')}.`,
      });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    if (file.size > MAX_BYTES) {
      setArchivo(null);
      setTipoArchivo('pdf');
      setMsg({
        type: 'error',
        text: `El archivo supera el límite de ${formatBytes(MAX_BYTES)} (tamaño: ${formatBytes(file.size)}).`,
      });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setArchivo(file);
    setTipoArchivo(ext);
  };
const handleSubmit = async (event) => {
  event.preventDefault();
  setMsg({ type: '', text: '' });

  if (!archivo) {
    setMsg({ type: 'error', text: 'No seleccionaste ningún archivo.' });
    if (inputRef.current) inputRef.current.focus();
    return;
  }

  const formData = new FormData();
  formData.append('archivo', archivo);
  formData.append('id_clase', dataClase.id_clase);
  formData.append('matricula_clase', dataClase.matricula_clase);
  formData.append('tipo_archivo', tipoArchivo);

  try {
    setIsUploading(true);
    setProgress(5);

    const response = await axios.post(subirArchivo, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      // IMPORTANTÍSIMO: no rechazar 4xx/5xx automáticamente. Así SIEMPRE mostramos algo.
      validateStatus: () => true,
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        setProgress(pct);
      },
    });

    // ÉXITO (2xx)
    if (response.status >= 200 && response.status < 300) {
      const okMsg =
        response?.data?.mensaje ||
        response?.data?.message ||
        'Archivo subido correctamente. Se ha agregado al listado.';
      setMsg({ type: 'success', text: okMsg });

      // Si el backend devuelve id_archivo, lo usamos para resaltar en la tabla
      const nuevoId = response?.data?.id_archivo || null;

      // Limpieza de formulario
      setArchivo(null);
      setTipoArchivo('pdf');
      if (inputRef.current) inputRef.current.value = '';

      // Refrescar lista y resaltar el nuevo (si tenemos id)
      await obtenerContenido();
      if (nuevoId) {
        setHighlightId(nuevoId);
        // quitar resaltado luego de 4s
        setTimeout(() => setHighlightId(null), 4000);
      }
      return;
    }

    // NO ÉXITO (4xx/5xx) → mostrar mensaje claro
    setMsg({ type: 'error', text: mapAxiosError(response) });
  } catch (error) {
    console.error('Error al subir el archivo:', error);
    setMsg({ type: 'error', text: mapAxiosError(error) });
  } finally {
    setIsUploading(false);
    setProgress(0);
    // asegura que el mensaje sea visible
    setTimeout(() => {
      const el = document.querySelector('.mensaje');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }
};
const descargarContenido = async (id_archivo, nombreArchivo) => {
  try {
    const url = descargarArchivo(id_archivo); // <-- mira que devuelva la URL completa correcta
    console.log('DESCARGAR =>', url);
    const resp = await axios.get(url, {
      responseType: 'blob',
      validateStatus: () => true,
    });

    

    console.log('status:', resp.status, 'ct:', resp.headers['content-type'], 'blob.size:', resp.data?.size);

    // Errores del server (4xx/5xx) → leer texto/JSON y mostrar
    if (resp.status >= 400) {
      const text = await (resp.data?.text ? resp.data.text() : new Response(resp.data).text());
      let msg = text;
      try { const j = JSON.parse(text); msg = j.mensaje || j.message || j.error || text; } catch {}
      setListMsg({ type: 'error', text: msg || `Error ${resp.status} al descargar.` });
      return;
    }

    // Si el content-type luce texto/json, probablemente es un error “bonito”
    const ct = (resp.headers['content-type'] || '').toLowerCase();
    if (!ct || ct.includes('application/json') || ct.includes('text/plain')) {
      const text = await (resp.data?.text ? resp.data.text() : new Response(resp.data).text());
      let msg = text;
      try { const j = JSON.parse(text); msg = j.mensaje || j.message || j.error || text; } catch {}
      setListMsg({ type: 'error', text: msg || 'El servidor devolvió un mensaje en lugar del archivo.' });
      return;
    }

    // Nombre desde Content-Disposition (si existe) o prop
    const cd = resp.headers['content-disposition'] || '';
    let filename = nombreArchivo || 'archivo';
    const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
    if (m) { try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; } }

    // 👉 No re-crear el Blob. Usar tal cual viene.
    const blob = resp.data;
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch (error) {
    console.error('Error al descargar contenido:', error);
    setListMsg({ type: 'error', text: mapAxiosError(error) });
  }
};



  const deleteContenidoClasesEntry = async (id_archivo) => {
    setListMsg({ type: '', text: '' });
    try {
      const response = await axios.delete(eliminarArchivo, {
        data: { id_archivo },
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200) {
        setArchivos((prev) => prev.filter((item) => item.id_archivo !== id_archivo));
        setListMsg({ type: 'success', text: 'Archivo eliminado correctamente.' });
      } else {
        setListMsg({ type: 'error', text: 'No se pudo eliminar el archivo.' });
      }
    } catch (error) {
      console.error('Error al eliminar el archivo:', error);
      setListMsg({ type: 'error', text: mapAxiosError(error) });
    }
  };

  const barra_superior = (
    <BarraSuperior
      texto_cabecera={dataClase.nombre_clase}
      redireccion={'teacher-home'}
      profile_redireccion={'t-profile-page'}
    />
  );

  return (
    <div className="contenedor-pagina">
      <Cabecera contenidosuperior={barra_superior} contenidoInferior={barra_inferior} />

      <div className="homework-container">
        <div className="h-holder">
          <h2>Subir contenido</h2>

          <form className="formulario-subida" onSubmit={handleSubmit}>
            <div className="campo">
              <label htmlFor="archivo">Seleccionar archivo</label>
              <input
                ref={inputRef}
                type="file"
                id="archivo"
                onChange={handleArchivoChange}
                accept={ALLOWED_EXTS.map((e) => '.' + e).join(',')}
              />
            </div>

            {archivo && (
              <div className="archivo-preview">
                <p><strong>Archivo:</strong> {archivo.name}</p>
                <p><strong>Tamaño:</strong> {formatBytes(archivo.size)}</p>
                <p><strong>Tipo:</strong> {tipoArchivo.toUpperCase()}</p>
              </div>
            )}

            {isUploading && (
              <div className="progreso">
                <div className="barra" style={{ width: `${progress}%` }} />
                <div className="etiqueta">{progress}%</div>
              </div>
            )}

            <button className="boton-subir" type="submit" disabled={isUploading}>
              {isUploading ? 'Subiendo…' : 'Subir archivo'}
            </button>
          </form>

          {msg.text && (
  <p
    role="alert"
    aria-live="assertive"
    className={`mensaje ${
      msg.type === 'success' ? 'mensaje-success'
      : msg.type === 'error' ? 'mensaje-error'
      : 'mensaje-info'
    }`}
  >
    {msg.text}
  </p>
)}


          <h3>Archivos subidos</h3>
          <table className="tabla-archivos">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Tamaño</th>
                <th>Descargar</th>
                <th>Eliminar</th>
              </tr>
            </thead>
            <tbody>
              {archivos.map((a, i) => (
                <tr key={i} className={a.id_archivo === highlightId ? 'fila-resaltada' : ''}>

                  <td>{a.nombre_archivo}</td>
                  <td>{a.tipo_archivo?.toUpperCase?.() || '-'}</td>
                  <td>{a.tamano_bytes ? formatBytes(a.tamano_bytes) : '-'}</td>
                  <td>
                    <button
                      className="boton-descargar"
                      onClick={() => descargarContenido(a.id_archivo, a.nombre_archivo)}
                    >
                      Descargar
                    </button>
                  </td>
                  <td>
                    <button
                      className="boton-eliminar"
                      onClick={() => deleteContenidoClasesEntry(a.id_archivo)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {archivos.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', opacity: 0.7 }}>
                    No hay archivos cargados para esta clase.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {listMsg.text && (
            <p className={`mensaje ${listMsg.type === 'success' ? 'mensaje-success' : listMsg.type === 'error' ? 'mensaje-error' : 'mensaje-info'}`}>
              {listMsg.text}
            </p>
          )}
        </div>
      </div>

      <PiePagina />
    </div>
  );
};
